import { useDeferredValue, useMemo, type ReactNode } from 'react'
import { getGenres, queryGames, SORT_OPTIONS } from '@/catalog/selectors'
import { useCatalog } from '@/catalog/useCatalog'
import { GameGrid } from '@/components/games/GameGrid'
import { DEFAULT_SORT, useLibraryQuery } from '@/components/games/useLibraryQuery'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import { SearchIcon } from '@/components/ui/icons'
import { cn } from '@/lib/cn'

function FilterChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'inline-flex h-8 shrink-0 items-center rounded-full px-3.5 text-sm font-medium ring-1 ring-inset transition-colors',
        active
          ? 'bg-volt text-volt-ink ring-volt'
          : 'bg-white/[0.04] text-fg-muted ring-line hover:bg-white/[0.08] hover:text-fg',
      )}
    >
      {children}
    </button>
  )
}

/** The arcade itself: the homepage is the game library. */
export default function LibraryPage() {
  const { games } = useCatalog()
  const { search, genre, sort, update, clear } = useLibraryQuery()

  const deferredSearch = useDeferredValue(search)
  const genres = useMemo(() => getGenres(games), [games])
  const results = useMemo(
    () => queryGames(games, { search: deferredSearch, genre, sort }),
    [games, deferredSearch, genre, sort],
  )
  const hasFilters = Boolean(search || genre)

  return (
    <div className="container-page pt-5 sm:pt-6">
      <title>Zarcade</title>
      <h1 className="sr-only">Zarcade games</h1>

      <div className="mb-5 flex items-center gap-3">
        <div
          role="group"
          aria-label="Filter by genre"
          className="-my-1 flex min-w-0 flex-1 gap-2 overflow-x-auto py-1 [scrollbar-width:none]"
        >
          <FilterChip active={!genre} onClick={() => update('genre', null)}>
            All
          </FilterChip>
          {genres.map((g) => (
            <FilterChip key={g.id} active={genre === g.id} onClick={() => update('genre', genre === g.id ? null : g.id)}>
              {g.label}
            </FilterChip>
          ))}
        </div>
        <label className="relative flex shrink-0 items-center">
          <span className="sr-only">Sort games</span>
          <select
            value={sort}
            onChange={(event) => update('sort', event.target.value === DEFAULT_SORT ? null : event.target.value)}
            className="h-8 cursor-pointer appearance-none rounded-full bg-transparent pr-8 pl-3 text-sm font-medium text-fg-muted ring-1 ring-line ring-inset hover:text-fg hover:ring-line-strong focus:ring-2 focus:ring-volt/70 focus:outline-none"
          >
            {SORT_OPTIONS.map((option) => (
              <option key={option.id} value={option.id} className="bg-ink-850 text-fg">
                {option.label}
              </option>
            ))}
          </select>
          <svg className="pointer-events-none absolute right-3 size-3.5 text-fg-subtle" viewBox="0 0 16 16" aria-hidden="true">
            <path d="m4 6 4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </label>
      </div>

      <p className="sr-only" aria-live="polite">
        {hasFilters ? `${results.length} of ${games.length} games` : `${results.length} games`}
      </p>

      {results.length > 0 ? (
        <GameGrid games={results} />
      ) : (
        <EmptyState
          icon={<SearchIcon />}
          title={games.length === 0 ? 'No games yet' : 'No games match'}
          action={
            hasFilters && (
              <Button variant="secondary" onClick={clear}>
                Clear filters
              </Button>
            )
          }
        >
          {games.length === 0
            ? 'Games will appear here as soon as they are published.'
            : 'Try a different title or remove a filter.'}
        </EmptyState>
      )}
    </div>
  )
}
